import React from 'react';
import { Box } from '@mui/material';
import { ChevronRight, Copy } from 'lucide-react';
import { AssetReadExternal, AssetSummaryAncestralExternal, AssetTypeReadExternal } from '@askporter/client-grieg-lyric';
import { captureException as defaultCaptureException } from '@askporter/exception-logger';
import { useToast } from '@askporter/toast';
import { copyToClipboard, getInitialsFromLongName } from '@askporter/utils';
import { Avatar, AvatarSize } from '../Avatar';
import { IconButton } from '../Buttons/IconButton';
import { Chip } from '../Chip';
import Icon, { IconSize } from '../Icon';
import { Typography } from '../Typography';

/**
 * The asset shapes the card renders from: either the full asset read, or the summary a task carries for each of
 * its linked assets. Everything the card shows — title, reference, media — is on both, and the summary is what a
 * viewer who may not read the asset itself has to work from.
 */
export type AssetCardAsset = AssetReadExternal | AssetSummaryAncestralExternal;

export interface AssetCardProps {
  /**
   * Translation function
   */
  t: (key: string, options?: Record<string, string | number>) => string;
  /**
   * The asset to display — provides the title, reference, type tag and "View asset" link target. Optional so
   * that an editable card can render its selector while the asset's details are still loading.
   */
  asset?: AssetCardAsset;
  /**
   * The asset's type, used for the type tag's label and icon
   */
  assetType?: AssetTypeReadExternal;
  /**
   * When true the asset title is shown as a static box; when false the editable selector passed as `children`
   * is rendered in its place (e.g. an asset autocomplete that lets the user change the asset)
   */
  readOnly?: boolean;
  /**
   * Optional section heading rendered above the card (e.g. "Asset")
   */
  heading?: string;
  /**
   * The editable asset selector rendered in place of the static title box when `readOnly` is false
   */
  children?: React.ReactNode;
  /**
   * Optional exception handler for clipboard failures (defaults to the shared exception logger)
   */
  captureException?: (error: Error) => void;
}

/**
 * Displays a single asset as a card: its avatar and title, reference (copyable), type tag, and a "View asset"
 * link that opens the asset profile in a new tab. In read-only mode the title is a static box; otherwise the
 * `children` selector (e.g. an asset autocomplete) is rendered so the asset can be changed.
 */
export const AssetCard: React.FC<AssetCardProps> = ({
  t,
  asset,
  assetType,
  readOnly = false,
  heading,
  children,
  captureException = defaultCaptureException,
}) => {
  const { showSuccess, showError } = useToast();

  // A read-only card with no asset would render an empty shell, so render nothing until the asset is available.
  if (readOnly && !asset) return null;

  const imagePath = asset?.media?.[0]?.file?.filePath;
  const iconPath = assetType?.icon?.iconPath;
  const assetTypeLabel = assetType?.displayName || assetType?.name;
  const assetUrl = asset ? `/app/assets/${asset.uid}` : undefined;

  const handleCopy = () => {
    if (!assetUrl) return;
    const isSuccess = copyToClipboard(`${window.location.origin}${assetUrl}`, (...args: unknown[]) => {
      if (args[0] instanceof Error) captureException(args[0]);
    });
    if (isSuccess) {
      showSuccess(t('ns.asset_list:asset_link:clipboard_success'));
    } else {
      showError(t('ns.asset_list:asset_link:clipboard_error'));
    }
  };

  return (
    <Box>
      {heading && (
        <Typography sx={{ fontSize: '14px', fontWeight: 500, lineHeight: '20px', color: '#101828', mb: '6px' }}>
          {heading}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          p: '16px',
          borderRadius: '12px',
          backgroundColor: '#F5F5F5',
        }}
      >
        {readOnly
          ? asset && (
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, lineHeight: '20px', color: '#101828', mb: '6px' }}>
                  {t('ns.task_details:principal_asset_label')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    p: '12px 14px',
                    borderRadius: '8px',
                    border: '1px solid #D5D7DA',
                    backgroundColor: 'background.paper',
                    boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
                  }}
                >
                  <Avatar
                    dataTestId="asset-card-avatar"
                    altText={asset.title}
                    avatar={{
                      mode: imagePath ? 'image' : iconPath ? 'icon' : 'initials',
                      variant: 'rounded',
                      imagePath: imagePath ?? iconPath,
                      bgcolor: assetType?.icon?.iconColourMask,
                      initials: getInitialsFromLongName(asset.title),
                      size: { container: AvatarSize.XS, icon: IconSize.SM },
                    }}
                  />
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, lineHeight: '24px', color: '#101828' }}>
                    {asset.title}
                  </Typography>
                </Box>
              </Box>
            )
          : children}

        {asset?.assetRef && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: '20px', color: '#414651' }}>
              {asset.assetRef}
            </Typography>
            <IconButton
              size="small"
              aria-label={t('ns.asset_list:asset_link:aria_label')}
              onClick={handleCopy}
              sx={{ p: 0 }}
            >
              <Copy size={20} color="#414651" strokeWidth={1.5} absoluteStrokeWidth />
            </IconButton>
          </Box>
        )}

        {(assetTypeLabel || assetUrl) && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            {assetTypeLabel && (
              <Chip
                label={assetTypeLabel}
                color="default"
                size="small"
                variant="outlined"
                startAdornment={
                  iconPath ? { mode: 'icon', icon: <Icon fullPath={iconPath} size={IconSize.SM} /> } : undefined
                }
              />
            )}
            {assetUrl && (
              <Box
                component="a"
                href={assetUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="asset-card-view-asset"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '2px',
                  ml: 'auto',
                  textDecoration: 'none',
                  color: 'primary.main',
                  fontSize: '14px',
                  fontWeight: 600,
                  lineHeight: '20px',
                }}
              >
                {t('ns.task_details:asset:view_profile')}
                <ChevronRight size={24} strokeWidth={1.5} absoluteStrokeWidth />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
